import { el, money } from './ui'
import { cashWords, fromCredit } from './brought'

// Payment colours retain the workbook's meanings. Booking state is separate.
export function paymentLabel(payment: { payment_status?: string | null; payment_method?: string | null; chased?: number | null; amount_pence?: number | null; from_credit_pence?: number; paid_in_pence?: number; cash_taken_pence?: number; cash_unconfirmed?: number }): { text: string; className: string } {
  // A cached grid response from before payment aggregates existed is not
  // evidence that nobody recorded the payment.
  if (payment.payment_status == null) return { text: 'Payment unavailable', className: 'badge-unknown' }
  // Money brought from Squeegee (0043, src/brought.ts): cash an older app took with no amount, and cash taken that
  // was not the price. Absent fields: the badge as before.
  const cash = cashWords({ amount_pence: payment.amount_pence ?? null, ...payment }, money)
  if (payment.payment_status === 'paid') return payment.payment_method === 'cash'
    ? { text: cash ?? 'Paid cash', className: 'badge-cash' }
    // "Paid bank" only when bank was really chosen; a payment with no way recorded stays plain "Paid" (Astra, Liam's pick 5A).
    : { text: fromCredit({ amount_pence: null, ...payment }) && !payment.payment_method ? 'Paid from credit' : payment.payment_method === 'bank' ? 'Paid bank' : 'Paid', className: 'badge-paid' }
  // Part paid (cash under the price, or credit on it): what is still to pay.
  const on = Math.max(0, payment.paid_in_pence ?? 0) + fromCredit({ amount_pence: null, ...payment })
  if (payment.payment_status === 'unpaid' && on > 0 && payment.amount_pence != null) {
    // Its own pale yellow, as Part paid on an invoice (Liam, 22 Oct 2026: "Why is the not paid the same color as £15 to pay").
    return { text: `${money(Math.max(0, payment.amount_pence - on))} to pay`, className: payment.chased ? 'badge-chased' : 'badge-topay' }
  }
  // One set of words on every screen (Liam's pick 5A, 22 Oct 2026): "Not paid", "Chased · not paid".
  if (payment.payment_status === 'unpaid') return payment.chased
    ? { text: 'Chased · not paid', className: 'badge-chased' }
    : { text: 'Not paid', className: 'badge-unpaid' }
  return { text: 'Payment not recorded', className: 'badge-unknown' }
}

export function paymentBadge(payment: Parameters<typeof paymentLabel>[0]): HTMLElement {
  const label = paymentLabel(payment)
  return el('span', { class: `badge ${label.className}` }, label.text)
}

// What a price-hidden worker said they took in cash (0021), for the OWNER:
// "£55 cash taken", and when that is not the visit's price, by how much, so
// a short handover is seen on the day rather than at the month's end.
export function cashTakenBadge(v: { cash_collected_pence?: number | null; amount_pence?: number | null; payment_method?: string | null }): HTMLElement | null {
  if (v.cash_collected_pence == null || v.payment_method !== 'cash') return null
  const taken = v.cash_collected_pence
  const gap = v.amount_pence == null ? 0 : taken - v.amount_pence
  const words = gap === 0 ? `${money(taken)} cash taken` : `${money(taken)} cash taken · ${money(Math.abs(gap))} ${gap < 0 ? 'short' : 'over'}`
  return el('span', { class: `badge badge-cash-taken${gap < 0 ? ' badge-cash-short' : ''}` }, words)
}
